import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import MultiPage from './MultiPage';
import TruthEngine from './TruthEngine';
import SettleBinary from './components/SettleBinary';
import CreateBinaryMarket from './components/CreateBinaryMarket';
import Manifesto from './components/Manifesto';
import PasswordGate from './components/PasswordGate';
import DivergenceGauge from './components/DivergenceGauge';
import BettingPanel from './BettingPanel';
import GodModePanel from './GodModePanel';
import ActivityFeed from './ActivityFeed';
import NewsPanel from './NewsPanel';
import MarketSelector from './MarketSelector';
import Portfolio from './Portfolio';
import GlobalTicker from './GlobalTicker';
import FeedbackModal from './FeedbackModal';
import UserGuideModal from './UserGuideModal';
import { Link } from 'react-router-dom';
import { GhostIdentityProvider } from './lib/identity';
import WalletIdentityProvider from './global/WalletIdentityProvider';
import IdentityErrorBoundary from './components/Identity/IdentityErrorBoundary';
import ArenaFooter from './components/ArenaFooter';
import SmallScreenWarning from './components/SmallScreenWarning';
import { IS_GAME_MODE, IS_STRIPPED_DOWN } from './lib/mode';

// One-shot boot probe. Reports existence only (never the value) so the
// Architect can confirm on Vercel whether the Thirdweb clientId made
// it through the build. If this logs `false` in prod, the 401 on
// /global is a missing env var — not a Civic crash.
// eslint-disable-next-line no-console
console.log('Thirdweb Key Detected:', !!process.env.REACT_APP_TEMPLATE_CLIENT_ID);

// Dev-only: print the actual value so the Architect can verify the
// right key came through on localhost. Stripped in production by the
// NODE_ENV gate (prod is where the real key lives, so we never leak).
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.log(
    'Thirdweb clientId (dev only):',
    process.env.REACT_APP_TEMPLATE_CLIENT_ID
      || process.env.NEXT_PUBLIC_TEMPLATE_CLIENT_ID
      || process.env.VITE_TEMPLATE_CLIENT_ID
      || process.env.REACT_APP_THIRDWEB_CLIENT_ID
      || '(none)',
  );
}

// ─── Config ───────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Snap {
  vault:      number;
  tick:       number;
  lastUpdate: Date;
}

interface DivergencePayload {
  sentiment:  number;
  price:      number;
  divergence: number;
}

const INITIAL: Snap = { vault: 0.5000, tick: 0, lastUpdate: new Date() };

// ─── App ──────────────────────────────────────────────────────────────────────

const USERS = ['alice', 'bob', 'charlie'] as const;
type User = typeof USERS[number];

// Outer crash-fallback for /global. The IdentityErrorBoundary renders
// this element when the Wallet subtree or the dynamically-loaded
// Civic portal throws hard enough to escape its local boundaries.
// We clear any poisoned identity state (forced tiers, Civic's own
// localStorage entries) and hard-navigate to /local so the citizen
// lands on the Ghost-mode Agora instead of a black screen.
function IdentityCrashFallback(): React.JSX.Element {
  useEffect(() => {
    try {
      const doomed: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (key.startsWith('agoracle:') || key.startsWith('civic.') || key.startsWith('gateway_') || /civic/i.test(key)) {
          doomed.push(key);
        }
      }
      doomed.forEach(k => localStorage.removeItem(k));
    } catch {
      // Storage may be blocked (private browsing, etc); proceed to navigate anyway.
    }
    window.location.replace('/local');
  }, []);
  return (
    <div style={{
      minHeight:      '100vh',
      background:     '#020617',
      color:          '#fca5a5',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
      fontFamily:     'monospace',
      fontSize:       13,
      letterSpacing:  '0.18em',
      padding:        24,
      textAlign:      'center',
    }}>
      ⚠ IDENTITY SYSTEM FAILED · REDIRECTING TO LOCAL MODE…
    </div>
  );
}

export default function App(): React.JSX.Element {
  // Mode marker on <html> so CSS rules can react globally (cockpit
  // border, hazard-stripe badge). Set once at mount; root component
  // never unmounts, so cleanup is symmetric for completeness only.
  // MUST come before any conditional return (rules-of-hooks).
  useEffect(() => {
    if (!IS_GAME_MODE) return;
    document.documentElement.classList.add('mode-game');
    return () => { document.documentElement.classList.remove('mode-game'); };
  }, []);

  // Ghost vs Wallet: identity is pluggable per route. The Norwich Hub
  // paths ("/", "/multi", etc.) keep the original Ghost Citizen flow;
  // "/global" renders the same MultiPage but resolves identity from a
  // connected MetaMask wallet (see global/WalletIdentityProvider).
  const Ghost  = (node: React.ReactNode) => <GhostIdentityProvider>{node}</GhostIdentityProvider>;
  // Wallet routes mount WalletIdentityProvider only. Civic's portal
  // is dynamic-imported from inside MultiPage on user click — it
  // never touches this boot path. The IdentityErrorBoundary v2 here
  // is the outer safety net: if anything in the Wallet subtree (or
  // the dynamically-loaded Civic portal) hard-crashes, we evacuate
  // to /local so the citizen isn't trapped on a black screen.
  const Wallet = (node: React.ReactNode) => (
    <IdentityErrorBoundary fallback={<IdentityCrashFallback />}>
      <WalletIdentityProvider>{node}</WalletIdentityProvider>
    </IdentityErrorBoundary>
  );

  return (
    <>
      {/* ── JAILBREAK HAMMER (temporary) ──────────────────────────────
            Forces scroll on every route regardless of what any
            component or library tries to lock. Remove once the iPhone
            scroll is confirmed. */}
      <style>{'html, body, #root { height: auto !important; overflow: visible !important; position: static !important; }'}</style>
      <BrowserRouter>
        <Routes>
          {/* Public — visible on any host.
              Session B Phase C (2026-05-13): per DECISIONS.md
              2026-05-13 I-046 (Architect reclassification post-gate-4),
              the Manifesto is held back from the user-facing surface.
              On Arena (IS_STRIPPED_DOWN), /manifesto redirects to /.
              On Live, the route renders the Manifesto unchanged. */}
          <Route
            path="/manifesto"
            element={IS_STRIPPED_DOWN ? <Navigate to="/" replace /> : <Manifesto />}
          />

          {/* Architect · standalone Truth Engine dashboard. Gated
              behind PasswordGate to match /classic and /gauge; no
              identity provider needed — this is pure visualisation. */}
          <Route path="/truth-engine" element={<PasswordGate><TruthEngine /></PasswordGate>} />

          {/* Operator · binary market settlement (I-074 P3c). NOT wrapped
              in PasswordGate — that component is a no-op (returns children
              unconditionally), so it would imply a client-side gate that
              doesn't exist. The real security boundary is server-side: the
              /api/settle-binary endpoint enforces the OPERATOR_SECRET
              Bearer gate and holds the service-role key. The route renders
              freely; nothing it does can settle without that secret. */}
          <Route path="/settle-binary" element={<SettleBinary />} />

          {/* User-created binary markets (I-075 P2). Arena-only (the create
              RPC + binary engine live on the Arena Supabase); the component
              shows an Arena-only notice on Live. Calls the policy-gated
              create_user_binary_market RPC (min-subsidy + required oracle). */}
          <Route path="/create-binary" element={<CreateBinaryMarket />} />

          {/* Agoracle Global — wallet-backed identity (Base Sepolia testnet).
              In Arena (IS_GAME_MODE), the four-page regional structure is
              hidden per DECISIONS.md 2026-05-05; /global redirects to the
              Markets root. Live is unaffected. */}
          <Route
            path="/global"
            element={IS_GAME_MODE
              ? <Navigate to="/" replace />
              : <PasswordGate>{Wallet(<MultiPage />)}</PasswordGate>}
          />

          {/* Gated — Family Key required · Ghost Citizen identity */}
          <Route path="/"        element={<PasswordGate>{Ghost(<MultiPage />)}</PasswordGate>} />
          <Route path="/local"   element={<PasswordGate>{Ghost(<MultiPage />)}</PasswordGate>} />
          {/* /classic and /gauge render the legacy MainApp cockpit
              from the pre-I-046 architecture (Market Intelligence
              dial, Sentiment Oracle Injector, GOD MODE admin
              actions: RESOLVE YES/NO WINS, REFILL ALICE $1K,
              INJECT LIQUIDITY, MINT +$10K, RESET MARKET). These
              are GOD MODE admin surfaces — they must never be
              reachable by invitees on the Arena. Session B Phase C
              revision-3 (2026-05-13, gate-22/23 fourth-finding fix):
              redirect both to `/` in game mode (paralleling the
              /manifesto redirect from earlier in Phase C). The
              legacy MainApp routes stay reachable on Live for
              now; if Live also needs them gated, a follow-up can
              widen the redirect condition. */}
          <Route
            path="/classic"
            element={IS_GAME_MODE
              ? <Navigate to="/" replace />
              : <PasswordGate><MainApp /></PasswordGate>}
          />
          <Route path="/multi"   element={<PasswordGate>{Ghost(<MultiPage />)}</PasswordGate>} />
          <Route
            path="/gauge"
            element={IS_GAME_MODE
              ? <Navigate to="/" replace />
              : <PasswordGate><MainApp /></PasswordGate>}
          />
          <Route path="/*"       element={<PasswordGate>{Ghost(<MultiPage />)}</PasswordGate>} />
        </Routes>
      </BrowserRouter>
      <ArenaFooter />
      <SmallScreenWarning />
    </>
  );
}

function MainApp(): React.JSX.Element {
  const [activeMarketId,       setActiveMarketId]       = useState('');
  const [activeMarketQuestion, setActiveMarketQuestion] = useState('');
  const [snap,                 setSnap]                 = useState<Snap>(INITIAL);
  const [loading,              setLoading]              = useState(false);
  const [apiErr,               setApiErr]               = useState<string | null>(null);
  const [anchorVibe,           setAnchorVibe]           = useState(0.50);
  const [resolvedOutcome,      setResolvedOutcome]      = useState<string | null>(null);
  const [currentUser,          setCurrentUser]          = useState<User>('alice');
  const [feedbackOpen,         setFeedbackOpen]         = useState(false);
  const [guideOpen,            setGuideOpen]            = useState(false);
  const tickRef = useRef(0);

  useEffect(() => {
    // Nothing to fetch until the user picks a market
    if (!activeMarketId) return;

    let cancelled = false;

    setLoading(true);
    setSnap(INITIAL);
    setAnchorVibe(0.50);
    setApiErr(null);
    setResolvedOutcome(null);

    async function fetchInitial(): Promise<void> {
      try {
        const res = await fetch(`${API_BASE}/api/market/${activeMarketId}/divergence`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: DivergencePayload = await res.json();
        if (cancelled) return;
        tickRef.current += 1;
        setSnap({ vault: data.price, tick: tickRef.current, lastUpdate: new Date() });
        setAnchorVibe(data.sentiment);
        setApiErr(null);
      } catch (e) {
        // Divergence data is only available for hub.db markets — silently use
        // neutral defaults for Postgres-only markets instead of showing an error.
        if (!cancelled) setAnchorVibe(0.50);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchInitial();

    const socket = io(API_BASE);
    socket.on('market_update', (data: { market_id: string; price: number }) => {
      if (cancelled || data.market_id !== activeMarketId) return;
      tickRef.current += 1;
      setSnap({ vault: data.price, tick: tickRef.current, lastUpdate: new Date() });
      setLoading(false);
      setApiErr(null);
    });
    socket.on('vibe_update', (data: { market_id: string; new_vibe: number }) => {
      if (cancelled || data.market_id !== activeMarketId) return;
      setAnchorVibe(data.new_vibe);
    });
    socket.on('market_resolved', (data: { market_id: string; winning_outcome: string }) => {
      if (cancelled || data.market_id !== activeMarketId) return;
      setResolvedOutcome(data.winning_outcome);
    });
    socket.on('connect_error', (err: Error) => {
      if (!cancelled) setApiErr(`socket: ${err.message}`);
    });

    return () => {
      cancelled = true;
      socket.disconnect();
    };
  }, [activeMarketId]);

  function handleSelectMarket(id: string, title: string): void {
    setActiveMarketId(id);
    setActiveMarketQuestion(title);
  }

  return (
    <div
      style={{ backgroundColor: '#020617', minHeight: '100vh', width: '100%', overflowY: 'auto', overflowX: 'clip' }}
      className="flex flex-col items-center gap-2 p-3"
    >
      <style>{`
        @keyframes agora-beta-pulse {
          0%, 100% { box-shadow: 0 0 0 rgba(252,211,77,0.0); }
          50%      { box-shadow: 0 0 14px rgba(252,211,77,0.55); }
        }
        @media (max-width: 640px) {
          .agora-beta-pill {
            font-size:     12px !important;
            padding:       4px 12px !important;
            letter-spacing: 0.32em !important;
            color:         #fef3c7 !important;
            background:    rgba(252,211,77,0.22) !important;
            border-color:  #fcd34d !important;
            animation:     agora-beta-pulse 3s ease-in-out infinite;
          }
          .agora-oracle-pill {
            font-size:     13px !important;
            padding:       11px 22px 11px 18px !important;
            min-height:    44px !important;
            box-shadow:    0 0 18px rgba(124,58,237,0.55) !important;
            border-color:  #c4b5fd !important;
          }
          .agora-oracle-pill > span:first-child { font-size: 22px !important; }
        }
        @media (max-width: 380px) {
          .agora-oracle-pill {
            font-size:     10px !important;
            padding:       9px 12px 9px 10px !important;
            letter-spacing: 0.14em !important;
            gap:            6px !important;
          }
          .agora-oracle-pill > span:first-child { font-size: 16px !important; }
          .agora-beta-pill { font-size: 10px !important; padding: 3px 9px !important; letter-spacing: 0.26em !important; }
        }
      `}</style>
      {/* ── Session header ───────────────────────────────────────────────── */}
      <div
        className="w-full max-w-5xl flex items-center justify-between"
        style={{ borderBottom: '1px solid #1e293b', paddingBottom: 6 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{
            fontFamily:    "'Cormorant Garamond', 'Georgia', serif",
            fontSize:      16,
            color:         '#e0e7ff',
            letterSpacing: '0.01em',
          }}>
            Agoracle
          </span>
          <span className="agora-beta-pill" style={{
            fontFamily:    'monospace',
            fontSize:      8,
            fontWeight:    700,
            color:         '#fde68a',
            letterSpacing: '0.28em',
            padding:       '1px 7px',
            border:        '1px solid #a16207',
            borderRadius:  999,
            background:    'rgba(161,98,7,0.18)',
          }}>
            BETA
          </span>
          <span style={{ color: '#1e293b', fontSize: 9 }}>·</span>
          <span style={{ color: '#4b6080', fontSize: 10, letterSpacing: '0.2em', fontFamily: 'monospace' }}>
            NORWICH HUB&nbsp;·&nbsp;v1.0-ALPHA
          </span>
          <button
            onClick={() => setGuideOpen(true)}
            title="Oracle Guide — Manifesto, mechanics, and the rite of settlement"
            aria-label="Open Oracle Guide"
            className="agora-oracle-pill"
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              gap:             7,
              background:      'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              border:          '1px solid #a78bfa',
              borderRadius:    999,
              color:           '#faf5ff',
              fontSize:        10,
              fontWeight:      600,
              letterSpacing:   '0.18em',
              textTransform:   'uppercase',
              padding:         '5px 14px 5px 11px',
              cursor:          'pointer',
              fontFamily:      "'Inter', -apple-system, system-ui, sans-serif",
              boxShadow:       '0 0 12px rgba(124,58,237,0.32)',
              transition:      'transform 0.15s ease-out, box-shadow 0.15s, filter 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(167,139,250,0.55)'; e.currentTarget.style.filter = 'brightness(1.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';     e.currentTarget.style.boxShadow = '0 0 12px rgba(124,58,237,0.32)'; e.currentTarget.style.filter = 'none'; }}
          >
            <span aria-hidden="true" style={{
              fontFamily: "'Cormorant Garamond', 'Georgia', serif",
              fontSize:   16,
              lineHeight: 1,
              fontWeight: 500,
              fontStyle:  'italic',
              transform:  'translateY(-0.5px)',
            }}>
              ◈
            </span>
            Oracle Guide
          </button>
          <Link to="/" style={{
            background:    'none',
            border:        '1px solid #4c1d95',
            borderRadius:  4,
            color:         '#a78bfa',
            fontSize:      9,
            letterSpacing: '0.14em',
            padding:       '3px 9px',
            cursor:        'pointer',
            fontFamily:    'monospace',
            textDecoration:'none',
          }}>
            NERVE CENTER ›
          </Link>
          <select
            value={currentUser}
            onChange={e => setCurrentUser(e.target.value as User)}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-1 px-4 rounded-full border border-indigo-400 shadow-lg cursor-pointer outline-none transition-colors"
            style={{ fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.1em' }}
          >
            {USERS.map(u => (
              <option key={u} value={u}>{u.toUpperCase()}</option>
            ))}
          </select>
        </div>
        <span style={{ color: '#334155', fontSize: 9, fontFamily: 'monospace' }}>
          {loading ? (
            <span style={{ color: '#64748b' }}>CONNECTING…</span>
          ) : activeMarketId ? (
            <>
              <span style={{ color: '#475569' }}>TICK&nbsp;#{snap.tick.toString().padStart(4, '0')}</span>
              <span style={{ color: '#1e293b', margin: '0 6px' }}>·</span>
              {snap.lastUpdate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              {apiErr && <span style={{ color: 'rgba(239,68,68,0.5)', marginLeft: 8 }}>{apiErr}</span>}
            </>
          ) : null}
        </span>
      </div>

      {/* ── Market Selector ──────────────────────────────────────────────── */}
      <MarketSelector
        apiBase={API_BASE}
        activeMarketId={activeMarketId}
        currentUser={currentUser}
        onSelectMarket={handleSelectMarket}
      />

      {/* ── Global Ticker ────────────────────────────────────────────────── */}
      <div style={{ width: '100%', maxWidth: '100%' }}>
        <GlobalTicker apiBase={API_BASE} />
      </div>

      {/* ── No-selection placeholder ─────────────────────────────────────── */}
      {!activeMarketId && (
        <p style={{
          fontFamily:    'monospace',
          color:         '#2a3a4a',
          fontSize:      12,
          marginTop:     24,
          letterSpacing: '0.08em',
        }}>
          Select a market above to begin.
        </p>
      )}

      {/* ── Feedback button (fixed, bottom-right; sits above any active
            mobile drawer peek via --drawer-height) ──────────────────── */}
      <button
        onClick={() => setFeedbackOpen(true)}
        style={{
          position:        'fixed',
          bottom:          'calc(20px + var(--drawer-height, 0px))',
          right:           20,
          zIndex:          700,
          backgroundColor: '#4f46e5',
          border:          '1px solid #6366f1',
          borderRadius:    20,
          color:           '#e0e7ff',
          fontSize:        10,
          fontFamily:      'monospace',
          letterSpacing:   '0.12em',
          padding:         '7px 16px',
          cursor:          'pointer',
          boxShadow:       '0 0 16px rgba(99,102,241,0.35)',
          transition:      'bottom 0.32s cubic-bezier(.22,.61,.36,1)',
        }}
      >
        FEEDBACK
      </button>

      {feedbackOpen && (
        <FeedbackModal currentUser={currentUser} onClose={() => setFeedbackOpen(false)} />
      )}

      {guideOpen && (
        <UserGuideModal onClose={() => setGuideOpen(false)} initialTab="manifesto" />
      )}

      {/* ── Active market content ─────────────────────────────────────────── */}
      {activeMarketId && (
        <div className="flex flex-col lg:flex-row gap-8 w-full max-w-7xl mx-auto mt-6">

          {/* ── Left column: Market Intelligence ─────────────────────────── */}
          <div className="w-full lg:w-1/2 flex flex-col gap-6">
            <DivergenceGauge
              vibe={anchorVibe}
              vault={snap.vault}
              market={activeMarketQuestion}
              live={!loading}
              lastUpdate={snap.lastUpdate}
            />
            <NewsPanel marketId={activeMarketId} apiBase={API_BASE} />
          </div>

          {/* ── Right column: Execution Engine ───────────────────────────── */}
          <div className="w-full lg:w-1/2 flex flex-col gap-6">
            <Portfolio currentUser={currentUser} />
            <BettingPanel
              marketId={activeMarketId}
              marketQuestion={activeMarketQuestion}
              resolvedOutcome={resolvedOutcome}
              currentUser={currentUser}
            />
            <ActivityFeed marketId={activeMarketId} currentUser={currentUser} />
            <GodModePanel marketId={activeMarketId} currentUser={currentUser} />
          </div>

        </div>
      )}
    </div>
  );
}
